import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { safeBrandingUrl } from '../components/TenantBrand';
import { claimTenantTitle } from '../lib/documentTitleOwners';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Reserved subdomains that indicate main platform domain
// 'my' hosts the platform auth/user area — it is NOT a tenant panel
// 's' is the ShotLink screenshot host, s.justanotherpanels.com (added
// 2026-08-15). The backend keeps its OWN copy of this list in
// middleware/tenant.py - changing one does not affect the other.
const RESERVED_SUBDOMAINS = ['s', 'www', 'api', 'admin', 'app', 'my', 'mail', 'smtp', 'ftp', 'cdn', 'static'];
const PANEL_DOMAIN = 'justanotherpanels.com';
const MAIN_DOMAINS = [PANEL_DOMAIN, 'localhost', 'preview.emergentagent.com'];

/**
 * True when the host belongs to us (the platform domain or one of its
 * subdomains). Anything else is a customer's own domain pointed at this server.
 */
function isPanelDomainHost(hostname) {
  return ['smmclone.com', 'www.smmclone.com', 'my.smmclone.com'].includes(hostname) || hostname === PANEL_DOMAIN || hostname.endsWith(`.${PANEL_DOMAIN}`);
}

/**
 * Extract the tenant key from the current hostname.
 * Returns null if on main domain (no subdomain)
 */
function getSubdomainFromHost() {
  const hostname = window.location.hostname.toLowerCase();

  // Check if it's localhost or IP
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }

  // Check if it's a preview domain (e.g., smmpanel-host.preview.emergentagent.com)
  if (hostname.includes('preview.emergentagent.com')) {
    // No subdomain routing in preview - use query param or default
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tenant') || null;
  }

  // A customer's own domain: the WHOLE host is the tenant key, not its first
  // label. /api/tenant/by-subdomain matches subdomain OR custom_domain, so the
  // hostname is passed straight through. Without this, mypanel.com (two labels)
  // looked like the main domain and rendered the marketing site, while
  // panel.clientsite.com looked up the tenant "panel" and found nothing.
  // www is stripped because custom_domain is stored bare, even though the
  // certificate covers both names.
  if (!isPanelDomainHost(hostname)) {
    return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // Handle different domain structures
  // e.g., alfahad.justanotherpanels.com -> ['alfahad', 'smmclone', 'com']
  // e.g., justanotherpanels.com -> ['smmclone', 'com']

  if (parts.length <= 2) {
    // Main domain (e.g., justanotherpanels.com)
    return null;
  }

  // First part could be subdomain
  const potentialSubdomain = parts[0];

  // Check if it's a reserved subdomain
  if (RESERVED_SUBDOMAINS.includes(potentialSubdomain)) {
    return null;
  }

  return potentialSubdomain;
}

/**
 * Check if current domain is the main platform domain
 */
function isMainDomain() {
  const hostname = window.location.hostname.toLowerCase();

  // localhost is considered main domain
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // Check for tenant query param
    const urlParams = new URLSearchParams(window.location.search);
    return !urlParams.get('tenant');
  }

  // Preview domain without tenant param is main
  if (hostname.includes('preview.emergentagent.com')) {
    const urlParams = new URLSearchParams(window.location.search);
    return !urlParams.get('tenant');
  }

  // A customer's own domain is always a panel, never the platform.
  if (!isPanelDomainHost(hostname)) {
    return false;
  }

  // Check if it's a main domain without subdomain
  const parts = hostname.split('.');
  if (parts.length <= 2) {
    return true;
  }

  // Check if first part is reserved
  return RESERVED_SUBDOMAINS.includes(parts[0]);
}

// Default tenant context values
const defaultTenantContext = {
  tenant: null,
  tenantId: null,
  isLoading: true,
  error: null,
  isMainDomain: true,
  subdomain: null,
  branding: {
    name: 'SMMClone',
    logo_url: null,
    favicon_url: null,
    primary_color: '#7c3aed',
    currency: 'USD',
    currency_symbol: '$'
  },
  refetchTenant: () => {}
};

const TenantContext = createContext(defaultTenantContext);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const subdomain = getSubdomainFromHost();
  const mainDomain = isMainDomain();
  const identity = mainDomain ? 'platform' : subdomain;
  const [state, setState] = useState(() => ({ identity, tenant: null, isLoading: !mainDomain, error: null }));
  const pending = useRef({ identity, version: 0, controller: null });
  const title = useRef(null);
  // The initial render of a new host cannot expose the old response.
  if (pending.current.identity !== identity) {
    pending.current.controller?.abort();
    pending.current = { identity, version: pending.current.version + 1, controller: null };
  }

  const fetchTenant = useCallback(async (target, requestIdentity) => {
    if (pending.current.identity !== requestIdentity) return;
    pending.current.controller?.abort();
    const controller = new AbortController();
    const version = pending.current.version + 1;
    pending.current = { identity: requestIdentity, version, controller };
    const current = () => !controller.signal.aborted && pending.current.identity === requestIdentity && pending.current.version === version;
    if (!target || requestIdentity === 'platform') {
      setState({ identity: requestIdentity, tenant: null, isLoading: false, error: null });
      return;
    }
    // Refreshing saved branding must not unmount the current settings form.
    setState(previous => previous.identity === requestIdentity && previous.tenant
      ? { ...previous, isLoading: false, error: null }
      : { identity: requestIdentity, tenant: null, isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/api/tenant/by-subdomain/${encodeURIComponent(target)}`, { signal: controller.signal });
      if (!response.ok) throw new Error(response.status === 404 ? 'Panel not found' : 'Panel could not be loaded');
      const tenantData = await response.json();
      if (!tenantData || typeof tenantData !== 'object' || Array.isArray(tenantData)) throw new Error('Panel could not be loaded');
      if (current()) setState({ identity: requestIdentity, tenant: tenantData, isLoading: false, error: null });
    } catch (error) {
      if (current()) setState({ identity: requestIdentity, tenant: null, isLoading: false, error: error.message || 'Panel could not be loaded' });
    }
  }, []);

  useEffect(() => {
    fetchTenant(subdomain, identity);
    return () => { pending.current.controller?.abort(); pending.current.version += 1; };
  }, [fetchTenant, identity, subdomain]);

  const current = state.identity === identity;
  const tenant = current ? state.tenant : null;
  const isLoading = !current || state.isLoading;
  const error = current ? state.error : null;
  const active = tenant?.is_active === true;
  const faviconUrl = active ? safeBrandingUrl(tenant.favicon_url) : null;
  const primaryColor = active && /^#(?:[a-fA-F0-9]{3}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/.test(tenant.primary_color || '') ? tenant.primary_color : null;

  useEffect(() => {
    let favicon = document.head.querySelector('link[rel~="icon"]');
    const attributes = favicon ? [...favicon.attributes].map(attribute => [attribute.name, attribute.value]) : null;
    const previousColor = document.documentElement.style.getPropertyValue('--tenant-primary');
    const previousPriority = document.documentElement.style.getPropertyPriority('--tenant-primary');
    if (faviconUrl) {
      if (!favicon) { favicon = document.createElement('link'); favicon.rel = 'icon'; document.head.appendChild(favicon); }
      favicon.href = faviconUrl;
    }
    if (primaryColor) document.documentElement.style.setProperty('--tenant-primary', primaryColor);
    return () => {
      if (faviconUrl && favicon) {
        if (attributes) {
          [...favicon.attributes].forEach(attribute => favicon.removeAttribute(attribute.name));
          attributes.forEach(([name, value]) => favicon.setAttribute(name, value));
        } else favicon.remove();
      }
      if (primaryColor) {
        if (previousColor) document.documentElement.style.setProperty('--tenant-primary', previousColor, previousPriority);
        else document.documentElement.style.removeProperty('--tenant-primary');
      }
    };
  }, [faviconUrl, primaryColor]);

  useEffect(() => {
    const owner = claimTenantTitle(document.title);
    title.current = owner;
    return () => { owner.release(); title.current = null; };
  }, []);

  // The shared title owner keeps published SEO above this live tenant fallback.
  useEffect(() => {
    const nextTitle = active ? tenant.name || 'SMM Panel' : mainDomain ? 'JustAnotherPanels | The Best SMM Panels Platform' : title.current.baseline;
    title.current.update(nextTitle);
  }, [active, identity, mainDomain, tenant?.name]);

  const refetchTenant = useCallback(() => {
    const target = getSubdomainFromHost();
    return fetchTenant(target, isMainDomain() ? 'platform' : target);
  }, [fetchTenant]);

  // Build branding object from tenant data
  const branding = active ? {
    name: tenant.name || 'SMM Panel',
    logo_url: tenant.logo_url,
    favicon_url: tenant.favicon_url,
    primary_color: tenant.primary_color || '#7c3aed',
    currency: tenant.currency || 'USD',
    currency_symbol: tenant.currency_symbol || '$',
    support_email: tenant.support_email,
    support_telegram: tenant.support_telegram,
    registration_enabled: tenant.registration_enabled
  } : defaultTenantContext.branding;

  const value = {
    tenant,
    tenantId: tenant?.tenant_id || null,
    isLoading,
    error,
    isMainDomain: mainDomain,
    subdomain,
    branding,
    refetchTenant
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
